import { useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useLightboxLike } from "../shared/useLightboxLike";
import { useModelDisplayName } from "../shared/useModelDisplayName";
import { DATE_FORMAT_OPTIONS } from "../shared/constants";

interface UseVideoLightboxOptions {
  videoUrl: string;
  sourceImageUrl?: string;
  prompt?: string | null;
  createdAt?: string;
  model?: string | null;
  navigation?: {
    onNext: () => void;
    onPrev: () => void;
  };
}

export function useVideoLightbox({
  videoUrl,
  sourceImageUrl,
  prompt,
  createdAt,
  model,
  navigation,
}: UseVideoLightboxOptions) {
  const { t, locale } = useLanguage();

  const { liked, handleToggleLike } = useLightboxLike({
    mediaUrl: videoUrl,
    mediaType: "video",
    thumbnailUrl: videoUrl,
    referenceImageUrl: sourceImageUrl,
    prompt,
  });

  const modelDisplayName = useModelDisplayName(model, "video");

  // Keyboard arrow navigation
  useEffect(() => {
    if (!navigation) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") {
        e.preventDefault();
        navigation.onNext();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        navigation.onPrev();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [navigation]);

  const formattedDate = createdAt
    ? new Date(createdAt).toLocaleDateString(locale, DATE_FORMAT_OPTIONS)
    : new Date().toLocaleDateString(locale, DATE_FORMAT_OPTIONS);

  return {
    t,
    liked,
    formattedDate,
    modelDisplayName,
    handleToggleLike,
  };
}
